"use client"

import React from 'react'
import { useRouter } from 'next/navigation'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'

class ErrorBoundaryClass extends React.Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error) {
        // 更新 state 使下一次渲染能够显示降级后的 UI
        return { hasError: true, error }
    }

    componentDidCatch(error, errorInfo) {
        // 你可以将错误日志上报给服务器（如 Sentry 已经会自动捕获）
        console.error('ErrorBoundary caught an error:', error, errorInfo)
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null })
    }

    render() {
        if (this.state.hasError) {
            // 渲染降级 UI
            return (
                <ErrorFallback 
                    error={this.state.error} 
                    onRetry={this.handleRetry}
                    redirectPath={this.props.redirectPath}
                />
            )
        }

        return this.props.children
    }
}

// 降级 UI 组件（使用函数组件以便使用 hooks）
function ErrorFallback({ error, onRetry, redirectPath = '/projects' }) {
    const router = useRouter()

    const handleGoToProjects = () => {
        router.push(redirectPath)
    }

    // 检查是否是 DOM 操作相关的错误（通常由浏览器扩展引起）
    const isDOMError = error?.message?.includes('insertBefore') || 
                       error?.message?.includes('removeChild') ||
                       error?.message?.includes('appendChild') ||
                       error?.name === 'NotFoundError'

    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-8">
            <div className="bg-base-200 rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
                <div className="flex justify-center mb-4">
                    <div className="bg-warning/20 p-4 rounded-full">
                        <AlertTriangle className="w-12 h-12 text-warning" />
                    </div>
                </div>
                
                <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
                
                {isDOMError ? (
                    <div className="text-base-content/70 text-sm mb-6">
                        <p className="mb-2">
                            This may be caused by a browser extension (like translator or ad blocker) interfering with the page.
                        </p>
                        <p className="text-success">
                            ✓ Don't worry! Your task is likely still being processed.
                        </p>
                    </div>
                ) : (
                    <p className="text-base-content/70 text-sm mb-6">
                        An unexpected error occurred. Please try again.
                    </p>
                )}

                <div className="flex flex-col gap-3">
                    <button 
                        onClick={handleGoToProjects}
                        className="btn btn-primary w-full"
                    >
                        <Home className="w-4 h-4 mr-2" />
                        Go to My Projects
                    </button>
                    
                    <button 
                        onClick={onRetry}
                        className="btn btn-ghost w-full"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Try Again
                    </button>
                </div>

                {process.env.NODE_ENV === 'development' && error && (
                    <details className="mt-4 text-left">
                        <summary className="text-xs text-base-content/50 cursor-pointer">
                            Error details (dev only)
                        </summary>
                        <pre className="text-xs bg-base-300 p-2 rounded mt-2 overflow-auto max-h-32">
                            {error.message}
                        </pre>
                    </details>
                )}
            </div>
        </div>
    )
}

export default ErrorBoundaryClass

